import React from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

import { translateFr as tfr } from "@/i18n";
/** Report-style section: numbered eyebrow, title, purpose, generous spacing. */
export const ReportSection = ({ number, title, purpose, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
    className="space-y-6" data-testid={testid}>
    <div className="flex items-baseline gap-4">
      <span className="text-xs font-mono text-muted-foreground/60 tracking-[0.2em]">{number}</span>
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold tracking-[-0.01em]">{title}</h2>
        {purpose && <p className="text-sm text-muted-foreground mt-1">{purpose}</p>}
      </div>
    </div>
    {children}
  </motion.section>
);

/** +18% vs période précédente */
export const DeltaChip = ({ delta, suffix = tfr("vs période précédente"), invert = false, testid }) => {
  if (delta === null || delta === undefined) return null;
  const positive = invert ? delta < 0 : delta > 0;
  const neutral = delta === 0;
  const Icon = delta >= 0 ? TrendingUp : TrendingDown;
  return (
    <span data-testid={testid}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        neutral ? "text-muted-foreground border-white/[0.1]"
          : positive ? "text-green-500 border-green-500/25 bg-green-500/[0.06]"
            : "text-red-400 border-red-500/25 bg-red-500/[0.06]")}>
      <Icon className="h-3 w-3" />
      {delta > 0 ? "+" : ""}{delta}% <span className="text-muted-foreground font-normal hidden sm:inline">{suffix}</span>
    </span>
  );
};

export const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold mt-0.5 tabular-nums">{value ?? "—"}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

/** Honest placeholder for signals not yet available (Yousign / AI — Phase 2). */
export const Phase2Note = ({ children, testid }) => (
  <div className="flex items-center gap-2.5 rounded-xl border border-dashed border-white/[0.1] px-4 py-3 text-muted-foreground" data-testid={testid}>
    <Lock className="h-3.5 w-3.5 shrink-0" />
    <p className="text-xs leading-relaxed">{children}</p>
  </div>
);

export const Panel = ({ children, className, testid }) => (
  <div className={cn("rounded-2xl border border-white/[0.06] bg-surface p-5 sm:p-6", className)} data-testid={testid}>
    {children}
  </div>
);

export const fmtHours = (h) => {
  if (h === null || h === undefined) return null;
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${(h / 24).toFixed(1)} j`;
};
